<template>
  <form @submit.prevent="" class="form-financing">
    <div class="form-group mb-4 mt-4">
      <p>
        ¿El protocolo cuenta con financiamiento?: *
      </p>
      <div class="form-check form-check-inline">
        <input type="radio"
               name="has_financing"
               id="has-financing-yes"
               class="form-check-input"
               value="1"
               @change="showFinancingOptions"
               :checked="protocol.has_financing_information"
               :disabled="readonly || !editable">
        <label for="has-financing-yes" class="form-check-label">Sí</label>
      </div>

      <div class="form-check form-check-inline">
        <input type="radio"
               name="has_financing"
               id="has-financing-no"
               class="form-check-input"
               value=""
               @change="hideFinancingOptions"
               :checked="!protocol.has_financing_information"
               :disabled="readonly || !editable">
        <label for="has-financing-no" class="form-check-label">No</label>
      </div>
    </div>

    <div class="has-financing" :class="{ 'd-none': !protocol.has_financing_information && editable }">
      <p class="alert alert-info">
        <a href="#" @click.prevent="showRequestFunderModal">Si la financiadora no se encuentra en el listado, de clic aquí para solicitar su registro</a>
      </p>
      
      <div class="funders-table">
        <div v-if="editable" class="form-funder">
          <div class="form-group mb-4">
            <label for="financing-funder">Seleccionar financiadora</label>
            <select name="financing-funder" id="financing-funder"
                    class="form-control search-select-funders select2-input"
                    placeholder="Seleccione la financiadora"></select>
          </div>

          <div class="form-group mb-4">
            <label for="budget">Presupuesto (M.N.): *</label>
            <input type="number"
                   name="budget"
                   id="budget"
                   class="form-control"
                   v-model="budget"
                   placeholder="$0.00"/>
          </div>

          <div class="form-group mb-4">
            <button id="add-funder" type="button"
                    class="btn btn-subtle-primary"
                    @click="addFunder"
                    :disabled="savingFunder">
              <i class="fas fa-dollar-sign mr-2" v-if="!savingFunder"></i>
              <spinner small v-if="savingFunder"></spinner>
              Agregar financiadora
            </button>
          </div>
        </div>

        <funders-table v-if="protocol"
                       :funders="funders"
                       :protocol="protocol"
                       :readonly="readonly"
                       :editable="editable"
                       :attach-agreement-function="attachAgreementToFunder"
                       :delete-funder-function="removeFunder"
                       @add-agreement="funder => this.funder = funder"
                       @funder-updated="funderUpdated"></funders-table>
      </div>

      <hr>

      <div class="form-group mb-4">
        <label for="comments">Comentarios</label>
        <textarea name="comments"
                  id="comments"
                  class="form-control"
                  placeholder="Anexe comentarios en caso de ser necesario"
                  rows="5"
                  cols="5"
                  v-if="protocol && protocol.financing_information"
                  v-model="protocol.financing_information.comments"
                  :disabled="readonly || !editable"></textarea>
      </div>

      <div class="my-4">
        <financing-commitments-list :protocol="protocol"
                                    :editable="editable"
                                    :editable-commitments="editableCommitments"
                                    :financing-commitments="financingCommitments"
                                    @add-financing-commitment="showAddCommitmentDialog"
                                    @remove-commitment="showRemoveCommitmentDialog"
                                    @edit-commitment="showEditCommitmentDialog"></financing-commitments-list>
      </div>
    </div>

    <div class="has-not-financing" :class="{ 'd-none': protocol.has_financing_information}">
      <div class="form-group mb-4">
        <label for="other_means">Especificación de los medios para llevar a cabo el estudio: *</label>
        <textarea
          name="other_means"
          id="other_means"
          class="form-control"
          placeholder="Escriba su respuesta"
          rows="5"
          cols="5"
          v-if="protocol && protocol.financing_information"
          v-model="protocol.financing_information.other_means"></textarea>
      </div>
    </div>

    <div class="buttons-container" v-if="!readonly && editable">
      <button type="submit" class="btn btn-primary btn-lg mr-2"
              :disabled="savingFinancingInfo"
              @click="saveFinancingInformation">
        <span class="spinner-border text-yellow-500 spinner-border-sm" role="status" v-if="savingFinancingInfo">
          <span class="sr-only">Guardando...</span>
        </span>
        Guardar financiamiento
      </button>
    </div>

    <portal to="financing-modals">
      <financing-commitment-modal ref="financing-commitment-modal"
                                  type="protocol"
                                  :funders="funders"
                                  :selected-commitment="selectedCommitment"
                                  :add-commitment-function="addCommitment"
                                  :edit-commitment-function="editCommitment"></financing-commitment-modal>

      <ok-cancel-dialog id="delete-commitment-modal"
                        title="Eliminar compromiso"
                        content="<p>¿Está seguro de eliminar el compromiso contraido?</p>"
                        ok-button-text="Eliminar"
                        ok-button-type="danger"
                        @ok-button-pressed="deleteCommitment"
                        @cancel-button-pressed="commitment = null"
                        ref="delete-commitment-modal"></ok-cancel-dialog>
  
      <request-funder-modal :funder-saved="closeFunderModal"/>
    </portal>
  </form>
</template>

<script>
  import FundersTable from './FundersTable'
  import FinancingCommitmentsList from './FinancingCommitmentsList'
  import FinancingCommitmentModal from './FinancingCommitmentModal'
  import OkCancelDialog from '../ui/OkCancelDialog'
  import autosize from 'autosize'
  import { mapActions, mapGetters } from 'vuex'
  import { AlertManager } from '../utils/AlertManager'
  import { find } from 'lodash'
  import 'jquery-validation'
  import 'select2'
  import 'select2/dist/js/i18n/es'
  import Spinner from '../ui/Spinner'
  import RequestFunderModal from './RequestFunderModal'

  export default {
    name: 'ProtocolFinancing',
    components: {
      Spinner,
      FundersTable,
      FinancingCommitmentsList,
      FinancingCommitmentModal,
      OkCancelDialog,
      RequestFunderModal,
    },
    props: {
      protocol: {
        type: Object,
        required: true,
      },
      editable: {
        type: Boolean,
        default: true,
      },
      editableCommitments: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      hasFinancing: null,
      budget: '',
      selectedFunder: null,
      savingFunder: false,
      comments: null,
      otherMeans: null,
      savingFinancingInfo: false,
      commitment: null,
      funder: null,
      selectedFunders: [],
      selectedCommitment: null,
    }),
    methods: {
      ...mapActions({
        saveProtocolFinancing: 'protocols/saveProtocolFinancing',
        syncFunders: 'protocols/syncFunders',
        removeFunderFromProtocol: 'protocols/removeFunder',
        saveAgreementFile: 'protocols/attachAgreementToFunder',
        getFunders: 'protocols/getProtocolFunders',
        getFinancingCommitments: 'protocols/getFinancingCommitments',
        addFinancingCommitment: 'protocols/addFinancingCommitment',
        editFinancingCommitment: 'protocols/editFinancingCommitment',
        removeFinancingCommitment: 'protocols/removeCommitment',
      }),

      showFinancingOptions () {
        this.hasFinancing = true
        this.protocol.has_financing_information = true

        const $form = $('.form-financing')

        $form.find('#other_means').rules('remove')

        $('.has-not-financing').addClass('d-none')
        $('.has-financing').removeClass('d-none')
      },
      hideFinancingOptions () {
        this.hasFinancing = false
        this.protocol.has_financing_information = false

        const $form = $('.form-financing')

        $form.find('#other_means').rules('add', {
          required: true,
          messages: {
            required: 'Este campo es requerido',
          },
        })

        $('.has-not-financing').removeClass('d-none')
        $('.has-financing').addClass('d-none')
      },

      async saveFinancingInformation () {
        if (!$('.form-financing').valid()) {
          return false
        }

        // Check if user has added commitments.
        if (this.financingCommitments.length === 0 && this.protocol.has_financing_information) {
          AlertManager.error('Para guardar el financiamiento es necesario agregar los compromisos contraidos con las financiadoras')

          return false
        }

        this.savingFinancingInfo = true

        const response = await this.saveProtocolFinancing({
          protocol: this.protocol.id,
          has_financing: this.hasFinancing,
          other_means: this.hasFinancing ? null : this.protocol.financing_information.other_means,
          comments: !this.hasFinancing ? null : this.protocol.financing_information.comments,
        })

        if (response.status === 'error') {
          AlertManager.error(response.error)

          return
        }

        AlertManager.success(response.message)

        this.savingFinancingInfo = false

        this.$emit('saved-financing-information')

        await this.getFunders(this.protocol)

        await this.getFinancingCommitments(this.protocol)
      },
      async addFunder () {
        // Validate funders form with custom validations
        let validator = $('.form-funder').validate({
          ignore: [],
          highlight: function (element, errorClass, validClass) {
            $(element).addClass('is-invalid')
          },
          unhighlight: function (element, errorClass, validClass) {
            $(element).removeClass('is-invalid')
          },
          errorPlacement: function (error, element) {
            if (element.hasClass('select2-input')) {
              error.insertAfter(element.siblings('span.select2'))
            } else {
              error.insertAfter(element)
            }

            error.addClass('invalid-feedback')
          },
        })

        let errors    = {}
        validator.resetForm();

        if(!this.selectedFunder) {
          errors['financing-funder'] = 'Este campo es requerido'
        }

        if(!this.budget) {
          errors['budget'] = 'Este campo es requerido'
        }

        if (this.selectedFunder && find(this.selectedFunders, ['id', this.selectedFunder.id])) {
          errors['financing-funder'] = 'Esta financiadora ya fue asignada previamente al protocolo'
        }

        validator.showErrors(errors)

        if (this.budget && this.selectedFunder && !find(this.selectedFunders, ['id', this.selectedFunder.id])) {
          // Check if funder is not already added.
          this.selectedFunders.push({
            id: this.selectedFunder.id,
            title: this.selectedFunder.title,
            budget: this.budget,
          })

          this.budget         = null
          this.selectedFunder = null

          this.$search.val(null).trigger("change")

          this.savingFunder = true

          // Save funder in server.
          const response = await this.syncFunders({
            protocol: this.protocol,
            funders: this.selectedFunders,
          })

          this.savingFunder = false

          if (response.status === 'success') {
            AlertManager.success(response.message)

            return
          }

          AlertManager.error(response.error)
        }
      },

      async removeFunder (funder, callback) {
        const response = await this.removeFunderFromProtocol({
          protocol: this.protocol,
          funder: funder,
        })

        if (response.status === 'error') {
          AlertManager.error(response.error)
        }
        else {
          AlertManager.success(response.message)
          await this.getFinancingCommitments(this.protocol)
        }

        if (callback) callback(response)
      },

      async attachAgreementToFunder (files) {
        const formData = new FormData()

        files.forEach(file => {
          formData.append('files[]', file.file)
        })

        formData.append('protocol', this.protocol.id)
        formData.append('funder', this.funder.id)

        const response = await this.saveAgreementFile(formData)

        this.funder = null

        $('#funder-agreement-modal').modal('hide')

        if (response.status === 'error') {
          AlertManager.error(response.error)

          return
        }

        AlertManager.success(response.message)
      },

      showAddCommitmentDialog () {
        $('#financing-commitments-modal').modal('show')
      },

      showEditCommitmentDialog (commitment) {
        this.selectedCommitment = {
          id: commitment.financing_commitment_id,
          funder_id: commitment.protocol_funder_id,
          commitment_made: commitment.commitment_made,
          compliance_date: commitment.compliance_date,
        }
        
        $('#financing-commitments-modal').modal('show')
      },

      showRemoveCommitmentDialog (commitment) {
        this.commitment = commitment

        $('#delete-commitment-modal').modal('show')
      },

      async addCommitment (commitment, callback) {
        commitment.protocol = this.protocol

        const response = await this.addFinancingCommitment(commitment)

        if (response.status === 'success') {
          AlertManager.success(response.message)

          await this.getFinancingCommitments(this.protocol)
        }
        else {
          AlertManager.error(response.error)
        }

        if (callback) {
          callback()
        }

        $('#financing-commitments-modal').modal('hide')
      },

      async editCommitment (commitment, callback) {
        const response = await this.editFinancingCommitment(commitment)

        if (response.status === 'success') {
          AlertManager.success(response.message)

          await this.getFinancingCommitments(this.protocol)
        }
        else {
          AlertManager.error(response.error)
        }

        if (callback) {
          callback()
        }

        $('#financing-commitments-modal').modal('hide')
      },

      async deleteCommitment () {
        const {
                protocol,
                commitment,
              } = this

        const response = await this.removeFinancingCommitment({
          protocol,
          commitment,
        })

        if (response.status === 'error') {
          AlertManager.error(response.error)

          return
        }

        AlertManager.success(response.message)

        $('#delete-commitment-modal').modal('hide')

        await this.getFinancingCommitments(protocol)
      },

      async load () {
        await this.getFunders(this.protocol)
        await this.getFinancingCommitments(this.protocol)
      },

      async funderUpdated () {
        await this.getFunders(this.protocol)
        await this.getFinancingCommitments(this.protocol)

        $("#edit-funder-file-modal").modal("hide");
      },
  
      showRequestFunderModal() {
        $('#request-funder-modal').modal('show')
      },
  
      closeFunderModal() {
        $('#request-funder-modal').modal('hide')
      },
    },
    computed: {
      ...mapGetters({
        funders: 'protocols/funders',
        readonly: 'protocols/readonly',
        financingCommitments: 'protocols/financingCommitments',
      }),
    }
    ,
    created () {
      this.hasFinancing = this.protocol.has_financing_information

      if (this.readonly) {
        $('.form-financing').find(':input, textarea').prop('disabled', true)
      }
    },
    async mounted () {
      const vm = this

      autosize($('textarea'))

      await this.load()

      if (!this.protocol.financing_information) {
        this.protocol.financing_information = {}
      }

      $('.form-financing').validate({
        ignore: [],
        highlight: function (element, errorClass, validClass) {
          $(element).addClass('is-invalid')
        },
        unhighlight: function (element, errorClass, validClass) {
          $(element).removeClass('is-invalid')
        },
        errorPlacement: function (label, element) {
          label.addClass('invalid-feedback')
          label.insertAfter(element)
        },
        rules: {
          has_financing: {
            required: true,
            maxlength: 255,
          },
          other_means: {
            required: !this.hasFinancing,
            normalizer: function (value) {
              return $.trim(value)
            },
          },
          'funders[]': {
            required: this.hasFinancing,
          },
        },
        messages: {
          has_financing: {
            required: 'Este campo es requerido.',
            maxlength: 'El texto no debe superar los 255 caracteres.',
          },
          other_means: {
            required: 'Este campo es requerido.',
          },
          'funders[]': {
            required: 'Es necesario agregar por lo menos una financiadora.',
          },
        },
        submitHandler (form) {
          return false
        },
      })

      /**
       * select2 - Search funders
       */
      this.$search = $('.search-select-funders')
      this.$search.select2({
        placeholder: 'Seleccione la financiadora',
        ajax: {
          url: '/api/v1/funders',
          dataType: 'json',
          language: 'es',
          headers: {
            'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
          },
          data (params) {
            return {
              q: params.term,
              page: params.page || 1,
            }
          },
          processResults (data, params) {
            params.page = params.page || 1

            return {
              results: data.data,
              pagination: {
                more: (params.page * 10) < data.meta.total,
              },
            }
          },
        },
      })

      this.$search.on('select2:select', function (e) {
        vm.selectedFunder = e.params.data
      })
    },
  }
</script>
